import { ApiTask, TaskStatus, TaskType } from "griptonite-api-client/types/tasks";
import { createContext, useContext, useMemo } from "react";
import { create } from "zustand";
import { combine } from "zustand/middleware";

import { TaskSet } from "../../core/types";
import { useGradesQuery } from "../../hooks/grades";
import { useVenueQuery } from "../../hooks/venues";
import groupTasksByTypeGrade from "./groupTasksByTypeGrade";

export type TaskContextMenu = {
  task: ApiTask;
  x: number;
  y: number;
};

export const useTaskSetOverviewStore = create(
  combine(
    {
      selectedAssignee: null as string | null,
      viewBy: "status" as "grade" | "status",
      contextMenu: null as TaskContextMenu | null,
      lastAction: null as LastTaskAction | null,
      displayEditSidebar: false,
    },
    (set) => ({
      setSelectedAssignee: (assignee: string | null) => set({ selectedAssignee: assignee }),
      setViewBy: (viewBy: "grade" | "status") => set({ viewBy }),
      setContextMenu: (menuData: TaskContextMenu | null) => set({ contextMenu: menuData }),
      setLastAction: (action: LastTaskAction | null) => set({ lastAction: action }),
      setLastActionState: (mutationState: "loading" | "success" | "error", uuid?: string) =>
        set((state) => ({
          lastAction:
            state.lastAction && state.lastAction.changes.find((c) => c.task.uuid === uuid)
              ? { ...state.lastAction, mutationState }
              : null,
        })),
      setDisplayEditSidebar: (display: boolean) => set({ displayEditSidebar: display }),
    }),
  ),
);

export type LastTaskAction = {
  changes: {
    task: ApiTask;
    prevStatus: TaskStatus;
  }[];
  mutationState: "loading" | "success" | "error";
};

const TaskSetOverviewContext = createContext<
  | {
      taskSet: TaskSet | undefined;
      tasksByTypeGrade: Map<
        TaskType,
        { gradeValue: string | null; tasks: ApiTask[]; gradeSystem: string }[]
      >;
      tasksByTypeStatus: Map<TaskType, Map<TaskStatus, ApiTask[]>>;
      gradeSystem: string | undefined;
    }
  | undefined
>(undefined);

export const useTaskSetOverview = () => {
  const store = useTaskSetOverviewStore();
  const context = useContext(TaskSetOverviewContext);
  if (context === undefined) {
    throw new Error("useTaskSetOverview must be used within a TaskSetProvider");
  }
  return useMemo(() => ({ ...store, ...context }), [store, context]);
};

export const TaskSetProvider = ({
  taskSet,
  children,
}: {
  taskSet: TaskSet | undefined;
  children: React.ReactNode;
}) => {
  const store = useTaskSetOverviewStore();
  const { isFetched: gradesLoaded } = useGradesQuery();
  const { data: venue } = useVenueQuery(taskSet?.venue);
  const gradeSystem =
    taskSet?.climbType === "BOULDER"
      ? (venue?.boulderGradeSystem ?? undefined)
      : (venue?.sportGradeSystem ?? undefined);

  const tasksByTypeGrade = useMemo(() => {
    const defaultResult = new Map<TaskType, never[]>([
      ["SET", []],
      ["STRIP", []],
      ["MISC", []],
    ]);

    if (!gradesLoaded) {
      return defaultResult;
    }

    const tasks = (taskSet?.tasks || []).filter((task) =>
      store.selectedAssignee
        ? task.assignees?.find((a) => a.uuid === store.selectedAssignee)
        : true,
    );

    return groupTasksByTypeGrade(tasks);
  }, [gradesLoaded, taskSet?.tasks, store.selectedAssignee]);

  const tasksByTypeStatus = useMemo(() => {
    const tasksByTypeStatus = new Map<TaskType, Map<TaskStatus, ApiTask[]>>();

    taskSet?.tasks?.forEach((task) => {
      if (
        store.selectedAssignee &&
        !task.assignees.find((a) => a.uuid === store.selectedAssignee)
      ) {
        return;
      }
      if (!tasksByTypeStatus.has(task.type)) {
        tasksByTypeStatus.set(task.type, new Map());
      }

      const statusMap = tasksByTypeStatus.get(task.type)!;

      if (!statusMap.has(task.status)) {
        statusMap.set(task.status, []);
      }

      const statusTasks = statusMap.get(task.status)!;
      statusTasks.push(task);
    });

    return tasksByTypeStatus;
  }, [taskSet?.tasks, store.selectedAssignee]);

  const context = useMemo(
    () => ({
      taskSet,
      tasksByTypeGrade,
      tasksByTypeStatus,
      gradeSystem,
    }),
    [taskSet, tasksByTypeGrade, tasksByTypeStatus, gradeSystem],
  );

  return (
    <TaskSetOverviewContext.Provider value={context}>{children}</TaskSetOverviewContext.Provider>
  );
};
